import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, ArrowRight } from 'lucide-react';

export default function PastEvents() {
  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900">Past Events</h2>
          <p className="mt-4 text-lg text-gray-600">
            Highlights from our previous events and conferences
          </p>
        </motion.div>

        <div className="space-y-12">
          {/* CCDR Technical Workshop */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-7xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative flex items-center justify-center p-4 bg-gray-50">
                <img
                  src="/images/CCDR-Event.jpg"
                  alt="CCDR Technical Workshop"
                  className="w-full h-auto object-contain rounded-lg"
                  style={{ maxHeight: '600px' }}
                />
              </div>
              <div className="md:col-span-2 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Technical Workshop
                  </span>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar className="h-4 w-4 mr-2" />
                    May 9, 2024
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Country Climate Change and Development Report (CCDR) Technical Workshop
                </h3>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-5 w-5 mr-3" />
                    <span>Kampala Sheraton Hotel, Uganda</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="h-5 w-5 mr-3" />
                    <span>Organized by Ministry of Finance & World Bank</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">
                  A pivotal technical workshop that brought together experts to examine Uganda's climate finance landscape 
                  and develop program-based solutions. The workshop leveraged insights from over 20 comprehensive Country 
                  Climate Change and Development Reports across sub-Saharan Africa, focusing on strengthening resilience 
                  to climate risks and identifying opportunities in low-carbon development.
                </p>

                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-900">Key Highlights:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Examination of Uganda's climate finance landscape and program-based solutions</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Discussion of findings from over 20 CCDRs covering 25+ countries in sub-Saharan Africa</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Presentation of climate impact modeling and macro analytics results</span>
                    </li>
                  </ul>
                </div>

                <div className="text-sm text-gray-600 italic">
                  Led by Dr. Maria Nantongo and the CFSC team, contributing valuable insights to Uganda's climate finance strategy
                </div>
              </div>
            </div>
          </motion.div>

          {/* 1st Annual Conference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-7xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative flex items-center justify-center p-4 bg-gray-50">
                <img
                  src="/images/CFSC_Launch.jpeg"
                  alt="1st Annual Africa Climate Finance Conference"
                  className="w-full h-auto object-contain rounded-lg"
                  style={{ maxHeight: '600px' }}
                />
              </div>
              <div className="md:col-span-2 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Inaugural Conference
                  </span>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar className="h-4 w-4 mr-2" />
                    September 18-19, 2023
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  1st Annual Africa Climate Finance Conference
                </h3>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-5 w-5 mr-3" />
                    <span>Silver Spring Hotel, Bugolobi, Kampala, Uganda</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="h-5 w-5 mr-3" />
                    <span>Theme: Financing a Just Transition and Climate Resilience</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">
                  A groundbreaking inaugural conference that marked the official launch of the MUBS Climate Finance 
                  and Sustainability Centre. This historic event united stakeholders from across Africa to define 
                  climate finance priorities and establish a framework for sustainable development. The conference 
                  featured comprehensive discussions on financing just transition initiatives and building climate 
                  resilience, setting a strong foundation for future climate finance endeavors in Africa.
                </p>

                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-900">Key Highlights:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Launch of MUBS Sustainability and Climate Finance Centre</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Examination of Africa's stance on climate finance ahead of COP28</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Multi-stakeholder collaboration with key government ministries and organizations</span>
                    </li>
                  </ul>
                </div>

                <div className="text-sm text-gray-600 italic">
                  Co-organized by MUBS, ARIN, Ministry of Water and Environment, Ministry of Finance, 
                  CSBAG, and ACODE
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}